import { forwardRef, type ComponentPropsWithoutRef } from "react";
import clsx from "clsx";

import styles from "./stepper.module.css";
import { Card } from "../../shared/card/card.tsx";

export type StepperStep = {
  id: string;
  label: string;
};

export type StepperProps = Omit<ComponentPropsWithoutRef<"nav">, "children"> & {
  steps: StepperStep[];
  /** Zero-based index of the active step. Values are clamped to the step range when rendering. */
  currentStepIndex: number;
};

function clampIndex(index: number, length: number): number {
  if (length <= 0) {
    return 0;
  }
  return Math.min(Math.max(index, 0), length - 1);
}

export const Stepper = forwardRef<HTMLElement, StepperProps>(function Stepper(
  {
    steps,
    currentStepIndex,
    className,
    "aria-label": ariaLabel = "Steps",
    ...rest
  },
  ref
) {
  if (steps.length === 0) {
    return null;
  }

  const activeIndex = clampIndex(currentStepIndex, steps.length);

  return (
    <div
      ref={ref}
      className={clsx(styles.nav, className)}
      aria-label={ariaLabel}
      {...rest}
    >
      <Card>
        <ol className={styles.list}>
          {steps.map((step, index) => {
            const isComplete = index < activeIndex;
            const isCurrent = index === activeIndex;
            const segmentBeforeComplete = index > 0 && activeIndex >= index;
            const segmentAfterComplete = activeIndex > index;

            return (
              <li
                key={step.id}
                className={styles.item}
                aria-current={isCurrent ? "step" : undefined}
              >
                <div className={styles.trackRow}>
                  <div
                    className={clsx(
                      styles.segment,
                      styles.segmentBefore,
                      segmentBeforeComplete && styles.segmentComplete,
                    )}
                    aria-hidden
                  />
                  <span
                    className={clsx(
                      styles.dot,
                      isComplete && styles.dotComplete,
                      isCurrent && styles.dotCurrent,
                      !isComplete && !isCurrent && styles.dotUpcoming,
                    )}
                    aria-hidden
                  >
                    {index + 1}
                  </span>
                  <div
                    className={clsx(
                      styles.segment,
                      styles.segmentAfter,
                      segmentAfterComplete && styles.segmentComplete,
                    )}
                    aria-hidden
                  />
                </div>
                <span
                  className={clsx(
                    "body-sm",
                    styles.label,
                    isCurrent && styles.labelCurrent,
                    isComplete && styles.labelComplete,
                  )}
                >
                  {step.label}
                </span>
              </li>
            );
          })}
        </ol>
      </Card>
    </div>
  );
});

Stepper.displayName = "Stepper";
